import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import { AnimatePresence, Reorder } from 'motion/react';
import {
  Bars3Icon,
  BoltIcon,
  BriefcaseIcon,
  CubeIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  Squares2X2Icon,
  BookmarkIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/20/solid';

import { Input, InputGroup } from '@oysterjs/ui/input';
import { MountableSlideout } from '@oysterjs/ui/slideout';

import { CaraWorkspaceUseCase, type CustomPresetFieldsFragment } from '~/api/graphql';
import { ProUpgradeDialog } from '~/components/pro-upgrade-dialog';
import { pinKey, type HomePin } from '~/api/data/home/use-pinned-home-items';
import { CustomPresetInfoDialog, UseCaseInfoDialog } from './use-case-info-dialog';
import { CustomPresetCard, UseCaseChip, UseCaseCard } from './use-case-tile';
import { type TemplatePickerOption } from '~/components/cara-input/cara-input-template-picker-dialog';
import {
  AVAILABLE_USE_CASES,
  LINES_OF_BUSINESS,
  LINE_ORDER,
  USE_CASE_CATEGORIES,
  familyForPreset,
  searchTerms,
  useCaseById,
  useCaseSearchScore,
  useCasesInFamily,
  type LineOfBusiness,
  type RunCardTemplateKind,
  type UseCaseCategory,
  type UseCaseSpec
} from './use-case-catalog';

const TRIGGER_CLASS = clsx(
  'inline-flex items-center gap-2 rounded-full border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-3 py-1.5',
  'text-xs font-medium text-neutral-700 dark:text-neutral-300 shadow-sm',
  'hover:border-neutral-300 dark:hover:border-white/20 hover:bg-neutral-50 dark:hover:bg-white/5',
  'data-[active]:border-primary-300 dark:data-[active]:border-primary-500/30 data-[active]:bg-primary-50 dark:data-[active]:bg-primary-500/10 data-[active]:text-primary-900 dark:data-[active]:text-primary-200'
);

const PANEL_CLASS = clsx(
  'z-20 mt-1 w-72 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-2 shadow-lg ring-1 ring-black/5 dark:ring-white/10',
  'origin-top transition data-[closed]:scale-95 data-[closed]:opacity-0',
  'data-[enter]:duration-100 data-[enter]:ease-out data-[leave]:duration-75 data-[leave]:ease-in'
);

const CATEGORY_ORDER: UseCaseCategory[] = ['analyze', 'verify', 'produce'];

type Filters = {
  categories: UseCaseCategory[];
  families: string[];
  lines: LineOfBusiness[];
};

const EMPTY_FILTERS: Filters = { categories: [], families: [], lines: [] };

const FilterChip = <T extends string>({
  label,
  emptyLabel,
  icon: Icon,
  options,
  selected,
  onToggle
}: {
  label: string;
  emptyLabel: string;
  icon: React.ComponentType<{ className?: string }>;
  options: Array<{ value: T; label: string }>;
  selected: T[];
  onToggle: (value: T) => void;
}) => {
  const chosen = options.find((o) => o.value === selected[0])?.label;
  const summary =
    selected.length === 0
      ? emptyLabel
      : selected.length === 1
        ? (chosen ?? emptyLabel)
        : `${chosen} +${selected.length - 1}`;

  return (
    <Headless.Popover className="relative">
      <Headless.PopoverButton
        className={TRIGGER_CLASS}
        data-active={selected.length > 0 ? '' : undefined}
        aria-label={selected.length === 0 ? `${label}: any` : `${label}: ${summary}`}
      >
        <Icon className="size-4" />
        <span className="max-w-40 truncate">{summary}</span>
        <ChevronDownIcon className="size-3.5 text-neutral-400 dark:text-neutral-500" />
      </Headless.PopoverButton>
      <Headless.PopoverPanel transition anchor="bottom start" className={PANEL_CLASS}>
        <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
          {label}
        </div>
        <div className="flex max-h-72 flex-col overflow-y-auto">
          {options.map((option) => {
            const isSelected = selected.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => onToggle(option.value)}
                className={clsx(
                  'flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm',
                  isSelected
                    ? 'bg-primary-50 dark:bg-primary-500/10 font-medium text-primary-900 dark:text-primary-200'
                    : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-white/5'
                )}
              >
                <span className="min-w-0 truncate">{option.label}</span>
                {isSelected && (
                  <CheckIcon className="size-4 shrink-0 text-primary-600 dark:text-primary-400" />
                )}
              </button>
            );
          })}
        </div>
      </Headless.PopoverPanel>
    </Headless.Popover>
  );
};

/**
 * The arrangement itself: drag or arrow-key to reorder, X to remove. Exported
 * because the profile settings page arranges a home screen with the same
 * gestures, and two implementations of a drag list would drift.
 */
export const PinnedTray = ({
  pinned,
  surfaceLabel,
  titleOf,
  onReorder,
  onRemove,
  expanded,
  onToggleExpanded
}: {
  pinned: HomePin[];
  surfaceLabel: string;
  titleOf: (pin: HomePin) => string;
  onReorder: (next: HomePin[]) => void;
  onRemove: (pin: HomePin) => void;
  expanded: boolean;
  /**
   * Omit where the tray is the whole control rather than a strip above a
   * catalog: a settings section has room to keep the list open, and a
   * collapse toggle there only hides the thing the section is for.
   */
  onToggleExpanded?: () => void;
}) => {
  // Keyboard reorder: drag is pointer-only.
  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= pinned.length) {
      return;
    }
    const next = [...pinned];
    [next[index], next[target]] = [next[target], next[index]];
    onReorder(next);
  };

  return (
    <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 px-3.5 py-2.5">
      <div className="flex items-center gap-2">
        <BookmarkIcon className="size-4 text-primary-500 dark:text-primary-400" />
        <span className="text-xs font-semibold text-neutral-900 dark:text-white">
          On {surfaceLabel}
        </span>
        <span className="inline-flex items-center rounded-full bg-neutral-200 dark:bg-neutral-700 px-1.5 text-xs font-semibold text-neutral-600 dark:text-neutral-400">
          {pinned.length}
        </span>
        <div className="flex-1" />
        {onToggleExpanded && (
          <Headless.Button
            type="button"
            onClick={onToggleExpanded}
            aria-expanded={expanded}
            className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
          >
            {expanded ? 'Done' : 'Arrange'}
            <ChevronDownIcon className={expanded ? 'size-3.5 rotate-180' : 'size-3.5'} />
          </Headless.Button>
        )}
      </div>
      {expanded && (
        <>
          <Reorder.Group
            as="div"
            axis="y"
            values={pinned}
            onReorder={onReorder}
            role="list"
            aria-label="Pinned presets, in order"
            className="mt-3 flex flex-col gap-1.5"
          >
            <AnimatePresence initial={false}>
              {pinned.map((pin, index) => {
                const title = titleOf(pin);
                return (
                  <Reorder.Item
                    key={pinKey(pin)}
                    value={pin}
                    as="div"
                    tabIndex={0}
                    role="listitem"
                    aria-label={`${title}, position ${index + 1} of ${pinned.length}. Use the arrow keys to move it.`}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                        e.preventDefault();
                        move(index, e.key === 'ArrowUp' ? -1 : 1);
                      }
                    }}
                    className="flex cursor-grab items-center gap-2 rounded-lg border border-neutral-200/50 dark:border-neutral-700/50 bg-white dark:bg-neutral-800 px-2.5 py-2 shadow-sm focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 active:cursor-grabbing"
                  >
                    <Bars3Icon className="size-3.5 shrink-0 text-neutral-300 dark:text-neutral-600" />
                    <span className="text-xs font-semibold tabular-nums text-neutral-500 dark:text-neutral-400">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-xs font-semibold text-neutral-900 dark:text-white">
                      {title}
                    </span>
                    {pin.kind === 'customPreset' && (
                      <CubeIcon className="size-3.5 shrink-0 text-purple-500 dark:text-purple-400" />
                    )}
                    <Headless.Button
                      type="button"
                      aria-label={`Remove ${title} from ${surfaceLabel}`}
                      onClick={() => onRemove(pin)}
                      className="shrink-0 rounded p-0.5 text-neutral-400 dark:text-neutral-500 hover:bg-neutral-100 dark:hover:bg-white/10 hover:text-neutral-600 dark:hover:text-neutral-400"
                    >
                      <XMarkIcon className="size-3.5" />
                    </Headless.Button>
                  </Reorder.Item>
                );
              })}
            </AnimatePresence>
          </Reorder.Group>
          <div className="mt-1.5 flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-neutral-300 dark:border-neutral-600 py-2 text-xs font-medium text-neutral-400 dark:text-neutral-500">
            <PlusIcon className="size-3.5" />
            Next pin lands here
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Mount inside an `AnimatePresence`. This variant is open by construction, so
 * the `showing`-driven slideout cannot animate in from the same commit that
 * creates it.
 */
export const UseCaseBrowser = (props: {
  onClose: () => void;
  title?: string;
  /** Defaults to every available use case. */
  useCases?: UseCaseSpec[];
  /** The pin props come as a set; without them the browser is a plain picker. */
  pinned?: HomePin[];
  isPinned?: (pin: HomePin) => boolean;
  onTogglePin?: (pin: HomePin) => void;
  onRemovePin?: (pin: HomePin) => void;
  customPresets: CustomPresetFieldsFragment[];
  libraries?: Partial<Record<RunCardTemplateKind, TemplatePickerOption[]>>;
  onReorder?: (next: HomePin[]) => void;
  /**
   * Omitted where the browser is arranging a home screen rather than working
   * one: an admin building a profile has no workspace to start. Without them
   * the cards keep their pin and info controls and lose only the launch target.
   */
  onRun?: (id: CaraWorkspaceUseCase) => void;
  onRunCustomPreset?: (id: string) => void;
  hasProAccess: boolean;
  /**
   * The home screen being arranged, as it reads mid-sentence: "On my home
   * page", "Remove Compare quotes and policies from my home page". An admin arranging a
   * profile is not working their own page, so the two differ.
   */
  surfaceLabel?: string;
}) => {
  const [query, setQuery] = React.useState('');
  const [filters, setFilters] = React.useState<Filters>(EMPTY_FILTERS);
  const [arranging, setArranging] = React.useState(false);
  const [explaining, setExplaining] = React.useState<
    { kind: 'useCase'; spec: UseCaseSpec } | { kind: 'preset'; preset: CustomPresetFieldsFragment }
  >();
  const [upselling, setUpselling] = React.useState<UseCaseSpec>();

  const toggleCategory = (value: UseCaseCategory) =>
    setFilters((prev) => ({
      ...prev,
      categories: prev.categories.includes(value)
        ? prev.categories.filter((c) => c !== value)
        : [...prev.categories, value]
    }));

  const toggleFamily = (value: string) =>
    setFilters((prev) => ({
      ...prev,
      families: prev.families.includes(value)
        ? prev.families.filter((f) => f !== value)
        : [...prev.families, value]
    }));

  const toggleLine = (value: LineOfBusiness) =>
    setFilters((prev) => ({
      ...prev,
      lines: prev.lines.includes(value)
        ? prev.lines.filter((l) => l !== value)
        : [...prev.lines, value]
    }));

  const useCases = props.useCases ?? AVAILABLE_USE_CASES;
  const { isPinned, onTogglePin } = props;
  const terms = searchTerms(query);
  const results = useCases.filter((useCase) => {
    if (filters.categories.length > 0 && !filters.categories.includes(useCase.category)) {
      return false;
    }
    if (filters.families.length > 0 && !filters.families.includes(useCase.preset)) {
      return false;
    }
    if (filters.lines.length > 0 && !filters.lines.some((line) => useCase.lines.includes(line))) {
      return false;
    }
    return useCaseSearchScore(useCase, terms) > 0;
  });

  const ranked =
    terms.length > 0
      ? [...results].sort((a, b) => useCaseSearchScore(b, terms) - useCaseSearchScore(a, terms))
      : results;

  const presetsByID = React.useMemo(
    () => new Map(props.customPresets.map((preset) => [preset.id, preset])),
    [props.customPresets]
  );

  const titleOf = React.useCallback(
    (pin: HomePin) =>
      pin.kind === 'useCase'
        ? (useCaseById(pin.id)?.title ?? pin.id)
        : (presetsByID.get(pin.id)?.title ?? pin.id),
    [presetsByID]
  );

  // Custom presets have no category/family/line, so a dimension filter would lie if they stayed listed.
  const dimensionFiltered =
    filters.categories.length > 0 || filters.families.length > 0 || filters.lines.length > 0;

  const presetResults = dimensionFiltered
    ? []
    : props.customPresets.filter((preset) => {
        const text = [
          preset.title,
          preset.description,
          preset.createdBy?.name ?? '',
          'custom preset'
        ]
          .join(' ')
          .toLowerCase();
        return terms.every((term) => text.includes(term));
      });

  const familyOptions = React.useMemo(
    () =>
      Array.from(new Set(useCases.map((useCase) => useCase.preset))).map((preset) => ({
        value: preset as string,
        label: familyForPreset(preset)?.title ?? preset
      })),
    [useCases]
  );

  return (
    <>
      <MountableSlideout onClose={props.onClose} size="2xl" title={props.title ?? 'Preset browser'}>
        <div className="flex flex-col gap-4">
          {props.pinned && props.onReorder && props.onRemovePin && (
            <PinnedTray
              pinned={props.pinned}
              surfaceLabel={props.surfaceLabel ?? 'my home page'}
              titleOf={titleOf}
              onReorder={props.onReorder}
              onRemove={props.onRemovePin}
              expanded={arranging}
              onToggleExpanded={() => setArranging((prev) => !prev)}
            />
          )}

          <InputGroup>
            <MagnifyingGlassIcon />
            <Input
              name="use-case-search"
              aria-label="Search presets"
              placeholder="Search presets"
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
            />
          </InputGroup>

          <div className="flex flex-wrap items-center gap-2">
            <FilterChip
              label="What it produces"
              emptyLabel="Any outcome"
              icon={Squares2X2Icon}
              options={CATEGORY_ORDER.map((category) => ({
                value: category,
                label: USE_CASE_CATEGORIES[category].label
              }))}
              selected={filters.categories}
              onToggle={toggleCategory}
            />
            <FilterChip
              label="Workflow family"
              emptyLabel="All families"
              icon={BoltIcon}
              options={familyOptions}
              selected={filters.families}
              onToggle={toggleFamily}
            />
            <FilterChip
              label="Line of business"
              emptyLabel="Any line"
              icon={BriefcaseIcon}
              options={LINE_ORDER.map((line) => ({
                value: line,
                label: LINES_OF_BUSINESS[line].label
              }))}
              selected={filters.lines}
              onToggle={toggleLine}
            />
          </div>

          {presetResults.length > 0 && (
            <>
              <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                Your agency&rsquo;s presets
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {presetResults.map((preset) => (
                  <CustomPresetCard
                    key={preset.id}
                    preset={preset}
                    pinned={isPinned?.({ kind: 'customPreset', id: preset.id })}
                    onRun={props.onRunCustomPreset && (() => props.onRunCustomPreset?.(preset.id))}
                    onTogglePin={
                      onTogglePin && (() => onTogglePin({ kind: 'customPreset', id: preset.id }))
                    }
                    onExplain={() => setExplaining({ kind: 'preset', preset })}
                  />
                ))}
              </div>
              <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                Built-in presets
              </h3>
            </>
          )}

          <div
            role="group"
            aria-label="Built-in presets"
            className="grid grid-cols-1 gap-3 pb-6 sm:grid-cols-2"
          >
            {ranked.map((useCase) => {
              const locked = !!useCase.pro && !props.hasProAccess;
              return (
                <UseCaseCard
                  key={useCase.id}
                  useCase={useCase}
                  pinned={isPinned?.({ kind: 'useCase', id: useCase.id })}
                  locked={locked}
                  onRun={
                    props.onRun &&
                    (() => (locked ? setUpselling(useCase) : props.onRun?.(useCase.id)))
                  }
                  onTogglePin={
                    onTogglePin && (() => onTogglePin({ kind: 'useCase', id: useCase.id }))
                  }
                  onExplain={() => setExplaining({ kind: 'useCase', spec: useCase })}
                />
              );
            })}
          </div>
        </div>
      </MountableSlideout>

      {/* Siblings of the drawer. A dialog inside the drawer's own panel would
          inherit its focus trap and animate out with it. They stack over the drawer
          and leave it mounted, because the reader came here to compare presets and
          answering "what is this one" should not cost them the list. */}
      {explaining?.kind === 'useCase' && (
        <UseCaseInfoDialog
          useCase={explaining.spec}
          onRun={
            !props.onRun || (explaining.spec.pro && !props.hasProAccess)
              ? undefined
              : () => {
                  const { spec } = explaining;
                  setExplaining(undefined);
                  props.onRun?.(spec.id);
                }
          }
          onClose={() => setExplaining(undefined)}
        />
      )}

      {explaining?.kind === 'preset' && (
        <CustomPresetInfoDialog
          preset={explaining.preset}
          onRun={
            props.onRunCustomPreset &&
            (() => {
              const { preset } = explaining;
              setExplaining(undefined);
              props.onRunCustomPreset?.(preset.id);
            })
          }
          onClose={() => setExplaining(undefined)}
        />
      )}

      {/* The same dialog the original launcher showed when someone ticked a Pro
          preset: what it is, what the rest of its family does, and the upgrade
          button. */}
      {upselling && (
        <ProUpgradeDialog
          open
          onClose={() => setUpselling(undefined)}
          title={`Unlock ${upselling.title} with Pro`}
          description={`${upselling.about.summary} Upgrade to Pro to put this preset, and everything else Pro includes, to work for your team.`}
          useCases={useCasesInFamily(upselling.preset).map((sibling) => ({
            id: sibling.id,
            title: sibling.title,
            description: sibling.description,
            icon: <UseCaseChip useCase={sibling} />
          }))}
        />
      )}
    </>
  );
};
